import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { SectionPanel } from '~/components/ui/SectionPanel';
import { sellerLabel } from '~/utils';
import {
  itemsByPlatformProductLengthId,
  moneyOrDash,
  offeredBundleQuantity,
} from './quoteDisplay';
import { quotesByTallyDeliveryPrice } from './quoteOrdering';
import { isAwaitingSellerResponse } from './quoteNegotiation';
import {
  SellerActionRow,
  type RenderQuoteActions,
} from './SellerQuoteActionSlots';
import { shipFromFacilityLabel } from '~/utils/facilityOptions';
import { QuoteSellerName } from './QuoteSellerName';
import { QuoteStatusLabel } from './QuoteStatusLabel';
import { SellerOfferNotice } from './SellerOfferNotice';
import { quoteOnSellerOffer } from './sellerOffer';
import type { GetRequestForQuoteWithQuotesQuery } from '~/graphql/graphql';
import type { ProductGroup } from '~/pages/rfq-view/itemGroups';
import {
  COMPARISON_GRID_ROW_CLASS,
  PLAIN_ROW_BACKGROUND_CLASS,
  rowBackgroundClass,
  SELLER_COLUMN_WIDTH_CLASS,
  STICKY_ATTRIBUTE_LABEL_CLASS,
  STICKY_ATTRIBUTE_LABEL_POSITION_CLASS,
} from './comparisonGridLayout';
import { unmaskPrimaryContact } from '~/api/sellerContacts';
import { SellerContacts } from './SellerContacts';

type Quote =
  GetRequestForQuoteWithQuotesQuery['quotesForRequestForQuote'][number];
type QuoteItem = Quote['items'][number];

type Seller = {
  quote: Quote;
  offer: Quote;
  shipFrom: string | null;
  itemByPlatformProductLengthId: Map<string, QuoteItem>;
};

type WholeRequestRow = Readonly<{
  key: string;
  label: string;
  renderCell: (seller: Seller) => ReactNode;
}>;

type VendorComparisonTableProps = Readonly<{
  quotes: Quote[];
  groups: ProductGroup[];
  renderQuoteActions: RenderQuoteActions;
}>;

function shipFromLabel(quote: Quote): string | null {
  const facility = quote.sellingOrganizationFacility;
  return facility
    ? shipFromFacilityLabel(facility, quote.sellingOrganization.facilities)
    : null;
}

export function VendorComparisonTable({
  quotes,
  groups,
  renderQuoteActions,
}: VendorComparisonTableProps) {
  const { t } = useTranslation('rfq-view');

  const sellers = useMemo<Seller[]>(() => {
    const quotesOnOffer = new Map(
      quotes.map((quote) => [quoteOnSellerOffer(quote), quote])
    );
    return quotesByTallyDeliveryPrice([...quotesOnOffer.keys()]).map(
      (quoteOnOffer) => ({
        quote: quotesOnOffer.get(quoteOnOffer) ?? quoteOnOffer,
        offer: quoteOnOffer,
        shipFrom: shipFromLabel(quoteOnOffer),
        itemByPlatformProductLengthId: itemsByPlatformProductLengthId(
          quoteOnOffer.items
        ),
      })
    );
  }, [quotes]);
  const orderedQuotes = useMemo(
    () => sellers.map(({ quote }) => quote),
    [sellers]
  );
  const wholeRequestRows: ReadonlyArray<WholeRequestRow> = [
    {
      key: 'shipFrom',
      label: t('rfq_view.ship_from_label'),
      renderCell: ({ shipFrom }) => shipFrom ?? '—',
    },
    {
      key: 'leadTime',
      label: t('rfq_view.lead_time_label'),
      renderCell: ({ quote }) =>
        quote.leadTimeDays != null
          ? t('rfq_view.lead_time_days', { count: quote.leadTimeDays })
          : '—',
    },
    {
      key: 'wholeRequestPickupPrice',
      label: t('rfq_view.whole_request_pickup_price_label'),
      renderCell: ({ offer }) => moneyOrDash(offer.offeredPickupPriceForTally),
    },
    {
      key: 'wholeRequestDeliveryPrice',
      label: t('rfq_view.whole_request_delivery_price_label'),
      renderCell: ({ offer }) =>
        moneyOrDash(offer.offeredDeliveryPriceForTally),
    },
  ];

  return (
    <SectionPanel title={t('rfq_view.seller_quotes_heading')}>
      <div className="overflow-x-auto">
        <table role="table" className="block w-max text-sm">
          <thead role="rowgroup" className="block">
            <tr
              role="row"
              className={`${COMPARISON_GRID_ROW_CLASS} text-left text-neut-sec`}
            >
              <th
                role="columnheader"
                className={`${STICKY_ATTRIBUTE_LABEL_CLASS} font-normal pb-2`}
              />
              {orderedQuotes.map((quote) => (
                <th
                  key={quote.id}
                  role="columnheader"
                  scope="col"
                  className={`${SELLER_COLUMN_WIDTH_CLASS} font-semibold text-neut-prim pb-2`}
                >
                  <QuoteSellerName quote={quote} />
                  <QuoteStatusLabel
                    quote={quote}
                    className="block text-xs font-normal text-neut-sec"
                  />
                  <SellerOfferNotice quote={quote} />
                  <SellerContacts
                    sellerName={sellerLabel(quote.sellingOrganization)}
                    recipients={quote.recipients}
                    primaryContactId={
                      unmaskPrimaryContact(quote.primaryContact)?.id
                    }
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody role="rowgroup" className="block">
            {wholeRequestRows.map((wholeRequestRow, rowIndex) => (
              <SellerRow
                key={wholeRequestRow.key}
                label={wholeRequestRow.label}
                backgroundClass={rowBackgroundClass(rowIndex)}
                sellers={sellers}
                renderCell={wholeRequestRow.renderCell}
              />
            ))}

            {groups.map((group, groupIndex) => (
              <ProductRows
                key={group.productId}
                group={group}
                sellers={sellers}
                startingRowIndex={startingRowIndexOfGroup(
                  groups,
                  groupIndex,
                  wholeRequestRows.length
                )}
              />
            ))}

            <SellerActionRow
              quotes={orderedQuotes}
              renderQuoteActions={renderQuoteActions}
              cellPaddingClass="py-2"
            />
          </tbody>
        </table>
      </div>
    </SectionPanel>
  );
}

function SellerRow({
  label,
  sellers,
  renderCell,
  backgroundClass = PLAIN_ROW_BACKGROUND_CLASS,
}: Readonly<{
  label: string;
  sellers: Seller[];
  renderCell: (seller: Seller) => ReactNode;
  backgroundClass?: string;
}>) {
  return (
    <tr
      role="row"
      className={`${COMPARISON_GRID_ROW_CLASS} ${backgroundClass}`}
    >
      <th
        role="rowheader"
        scope="row"
        className={`${STICKY_ATTRIBUTE_LABEL_POSITION_CLASS} ${backgroundClass} text-left font-normal py-1 text-neut-sec`}
      >
        {label}
      </th>
      {sellers.map((seller) => (
        <td
          key={seller.quote.id}
          role="cell"
          className={`${SELLER_COLUMN_WIDTH_CLASS} py-1 text-neut-prim`}
        >
          {isAwaitingSellerResponse(seller.quote) ? '—' : renderCell(seller)}
        </td>
      ))}
    </tr>
  );
}

function startingRowIndexOfGroup(
  groups: ProductGroup[],
  groupIndex: number,
  firstProductRowIndex: number
): number {
  return groups
    .slice(0, groupIndex)
    .reduce(
      (runningIndex, group) => runningIndex + group.rows.length,
      firstProductRowIndex
    );
}

function ProductRows({
  group,
  sellers,
  startingRowIndex,
}: Readonly<{
  group: ProductGroup;
  sellers: Seller[];
  startingRowIndex: number;
}>) {
  const { t } = useTranslation('rfq-view');

  return (
    <>
      <tr role="row" className="block">
        <th
          role="columnheader"
          scope="colgroup"
          className="block text-left font-semibold text-neut-prim pt-4 pb-1"
        >
          <span className="sticky left-0 inline-block bg-white">
            {group.productName}
            <span className="ml-2 text-xs font-normal text-neut-sec">
              {t('rfq_view.quantity_label')}
            </span>
          </span>
        </th>
      </tr>
      {group.rows.map((row, rowIndex) => (
        <SellerRow
          key={row.platformProductLengthId}
          label={row.displayValue}
          backgroundClass={rowBackgroundClass(startingRowIndex + rowIndex)}
          sellers={sellers}
          renderCell={(seller) => {
            const item = seller.itemByPlatformProductLengthId.get(
              row.platformProductLengthId
            );
            return item ? offeredBundleQuantity(item, row) : '—';
          }}
        />
      ))}
    </>
  );
}
